// SDE.2 — Dashboard "Meus Entregáveis".
// Protegido pela feature flag `smart_deliverables`.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  listUserDeliverables, regenerateOutdated, batchPreviewOutdated,
} from "@/lib/sde/index.functions";
import { trackDeliverableEvent } from "@/lib/sde/telemetry";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type { DeliverableCategory, DeliverableStatus } from "@/lib/sde/types";
import {
  FileText, CheckCircle2, AlertCircle, Circle, RefreshCw, Sparkles,
  ArrowRight, Clock, Lock, Play, XCircle,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/membros/entregaveis/")({
  head: () => ({
    meta: [
      { title: "Meus Entregáveis — Projeto 101" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EntregaveisIndex,
});

const STATUS_META: Record<DeliverableStatus, { label: string; icon: any; className: string; cta?: string }> = {
  ready:             { label: "Pronto",            icon: CheckCircle2, className: "bg-emerald-50 text-emerald-700 border-emerald-200", cta: "Ver documento" },
  outdated:          { label: "Desatualizado",     icon: RefreshCw,    className: "bg-amber-50 text-amber-800 border-amber-200",       cta: "Atualizar" },
  available:         { label: "Pronto para gerar", icon: Play,         className: "bg-blue-50 text-blue-700 border-blue-200",         cta: "Gerar agora" },
  generating:        { label: "Gerando…",          icon: Clock,        className: "bg-blue-50 text-blue-700 border-blue-200" },
  insufficient_data: { label: "Dados insuficientes", icon: AlertCircle, className: "bg-slate-50 text-slate-600 border-slate-200",      cta: "Preencher plano" },
  locked:            { label: "Bloqueado",         icon: Lock,         className: "bg-slate-50 text-slate-500 border-slate-200" },
  error:             { label: "Erro",              icon: XCircle,      className: "bg-red-50 text-red-700 border-red-200",             cta: "Ver detalhes" },
};

type FilterKey = "all" | "priority" | DeliverableStatus;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "Todos" },
  { key: "priority", label: "Prioritários" },
  { key: "ready", label: "Prontos" },
  { key: "available", label: "Prontos para gerar" },
  { key: "outdated", label: "Desatualizados" },
  { key: "insufficient_data", label: "Dados insuficientes" },
];

function EntregaveisIndex() {
  const enabled = useFeatureFlag("smart_deliverables");
  const listFn = useServerFn(listUserDeliverables);
  const previewFn = useServerFn(batchPreviewOutdated);
  const regenFn = useServerFn(regenerateOutdated);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [showBatch, setShowBatch] = useState(false);

  const q = useQuery({
    queryKey: ["sde", "list"],
    queryFn: () => listFn(),
    enabled,
  });

  const preview = useQuery({
    queryKey: ["sde", "batch-preview"],
    queryFn: () => previewFn(),
    enabled: enabled && showBatch,
  });

  const regen = useMutation({
    mutationFn: (keys?: string[]) => regenFn({ data: keys ? { keys } : {} }),
    onSuccess: (res) => {
      trackDeliverableEvent("sde.batch.regenerated", { updated: (res as any)?.updated ?? 0 });
      qc.invalidateQueries({ queryKey: ["sde"] });
      setShowBatch(false);
    },
  });

  const filteredCards = useMemo(() => {
    if (!q.data) return [];
    return q.data.cards.filter((c) => {
      if (filter === "all") return true;
      if (filter === "priority") return c.priority;
      return c.status === filter;
    });
  }, [q.data, filter]);

  if (!enabled) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <Sparkles className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 text-2xl font-semibold text-slate-800">Smart Deliverables Engine</h1>
        <p className="mt-2 text-sm text-slate-600">
          Esta camada está em homologação. Assim que for liberada para sua conta,
          seus entregáveis derivados do Business Plan aparecerão aqui automaticamente.
        </p>
        <Link to="/membros/meu-negocio" className="mt-6 inline-flex items-center gap-2 text-sm text-[#0C1B33] underline">
          Ir para Meu Negócio <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  if (q.isLoading || !q.data) {
    return <div className="mx-auto max-w-5xl px-4 py-10 text-sm text-slate-500">Carregando entregáveis…</div>;
  }

  if (!q.data.hasProject) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <FileText className="mx-auto h-8 w-8 text-slate-400" />
        <h1 className="mt-3 text-2xl font-semibold text-slate-800">Nenhum projeto ativo</h1>
        <p className="mt-2 text-sm text-slate-600">
          Escolha um negócio na Biblioteca para iniciar seu projeto e destravar os entregáveis.
        </p>
        <Link to="/membros/negocios" className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0C1B33] px-4 py-2 text-sm font-medium text-white">
          Explorar biblioteca <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  const { totals, totalAll } = q.data;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-[#1E7A5C]">Smart Deliverables</p>
        <h1 className="mt-1 text-3xl font-bold text-slate-900">Meus Entregáveis</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Documentos e materiais profissionais derivados automaticamente do seu Business Plan.
          Nada é editado manualmente — quando o plano muda, o entregável fica marcado como
          desatualizado até você regenerar.
        </p>
      </header>

      {/* Totais */}
      <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: "Total", value: totalAll },
          { label: "Prontos", value: totals.ready },
          { label: "Prontos p/ gerar", value: totals.available },
          { label: "Desatualizados", value: totals.outdated },
          { label: "Dados insuficientes", value: totals.insufficient_data },
        ].map((k, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-[11px] uppercase tracking-wide text-slate-500">{k.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{k.value}</p>
          </div>
        ))}
      </section>

      {totals.outdated > 0 ? (
        <div className="mb-6 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 text-sm text-amber-900">
            <AlertCircle size={18} />
            <span>
              <strong>{totals.outdated}</strong> entregável(is) precisam ser atualizados —
              o Business Plan mudou desde a última geração.
            </span>
          </div>
          <button
            onClick={() => { setShowBatch(true); trackDeliverableEvent("sde.batch.previewed", { count: totals.outdated }); }}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
          >
            <RefreshCw size={14} /> Revisar e atualizar
          </button>
        </div>
      ) : null}

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
        {FILTERS.map((f) => {
          const count = f.key === "all" ? totalAll : f.key === "priority"
            ? q.data.cards.filter((c) => c.priority).length
            : totals[f.key as DeliverableStatus] ?? 0;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-xs transition ${
                active ? "border-[#0C1B33] bg-[#0C1B33] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              {f.label} <span className="opacity-70">· {count}</span>
            </button>
          );
        })}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filteredCards.map((c) => {
          const sm = STATUS_META[c.status];
          const Icon = sm.icon;
          const clickable = c.status !== "locked";
          const body = (
            <div className={`h-full rounded-lg border bg-white p-4 transition ${clickable ? "hover:border-[#0C1B33]" : "opacity-60"} ${c.priority ? "ring-1 ring-[#1E7A5C]/20" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">
                    {(c.category as DeliverableCategory)}{c.priority ? " · prioritário" : ""}
                  </p>
                  <h3 className="mt-0.5 text-sm font-semibold text-slate-900">{c.title}</h3>
                </div>
                <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-medium ${sm.className}`}>
                  <Icon size={10} /> {sm.label}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-600 line-clamp-2">{c.description}</p>
              {c.reason ? (
                <p className="mt-3 text-[11px] text-slate-500 line-clamp-2">{c.reason}</p>
              ) : null}
              <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                {c.currentVersion > 0 ? (
                  <span>v{c.currentVersion} · {c.updatedAt ? new Date(c.updatedAt).toLocaleDateString("pt-BR") : "—"}</span>
                ) : (
                  <span>&nbsp;</span>
                )}
                {sm.cta && clickable ? (
                  <span className="inline-flex items-center gap-1 font-medium text-[#0C1B33]">{sm.cta} <ArrowRight size={11} /></span>
                ) : null}
              </div>
            </div>
          );
          if (!clickable) return <div key={c.key}>{body}</div>;
          return (
            <Link
              key={c.key}
              to="/membros/entregaveis/$key"
              params={{ key: c.key }}
              className="block"
              onClick={() => trackDeliverableEvent("sde.deliverable.viewed", { key: c.key, status: c.status })}
            >
              {body}
            </Link>
          );
        })}
        {filteredCards.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
            Nenhum entregável nesta categoria.
          </div>
        ) : null}
      </div>

      {/* Batch dialog */}
      {showBatch ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowBatch(false)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Regeneração em lote</h2>
              <p className="mt-1 text-xs text-slate-600">
                Revise as mudanças abaixo antes de atualizar. Cada entregável ganha uma nova versão salva.
              </p>
            </div>
            <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
              {preview.isLoading ? (
                <p className="text-sm text-slate-500">Analisando mudanças…</p>
              ) : (preview.data?.items ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">Tudo em dia — nenhum entregável precisa ser regenerado.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {preview.data!.items.map((it) => (
                    <li key={it.key} className="py-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{it.title}</p>
                          <p className="text-[11px] text-slate-500">
                            v{it.currentVersion} → v{it.nextVersion} · hash {it.previousHash.slice(0, 6)}…→{it.nextHash.slice(0, 6)}…
                          </p>
                        </div>
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                          {it.changedBlocks.length} bloco(s)
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
              <button onClick={() => setShowBatch(false)} className="rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
                Cancelar
              </button>
              <button
                onClick={() => regen.mutate(undefined)}
                disabled={regen.isPending || (preview.data?.items ?? []).length === 0}
                className="inline-flex items-center gap-2 rounded-md bg-[#0C1B33] px-4 py-2 text-sm font-medium text-white hover:bg-[#08152a] disabled:opacity-50"
              >
                <RefreshCw size={14} className={regen.isPending ? "animate-spin" : ""} />
                {regen.isPending ? "Atualizando…" : "Confirmar regeneração"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
